import React, { useEffect, useState } from 'react';
import {
	Box,
	VStack,
	Spinner,
	Alert,
	Collapse,
	IconButton,
	Flex,
	Image,
	Text,
	Link as ChakraLink,
	Tooltip,
	Divider,
	Badge,
} from '@chakra-ui/react';
import { Link, useLocation } from 'react-router-dom';
import {
	FaChevronLeft,
	FaChevronRight,
	FaTimes,
	FaEye,
	FaEyeSlash,
} from 'react-icons/fa';
import * as GoIcons from 'react-icons/go';
import * as FaIcons from 'react-icons/fa';
import * as MdIcons from 'react-icons/md';
import * as AiIcons from 'react-icons/ai';
import * as IoIcons from 'react-icons/io';
import * as Io5Icons from 'react-icons/io5';
import * as BsIcons from 'react-icons/bs';
import * as FiIcons from 'react-icons/fi';
import * as RiIcons from 'react-icons/ri';
import * as HiIcons from 'react-icons/hi';
import * as SiIcons from 'react-icons/si';
import * as GiIcons from 'react-icons/gi';
import * as BiIcons from 'react-icons/bi';
import * as CgIcons from 'react-icons/cg';
import * as TbIcons from 'react-icons/tb';
import * as TiIcons from 'react-icons/ti';
import * as ImIcons from 'react-icons/im';
import * as VscIcons from 'react-icons/vsc';
import * as Fa6Icons from 'react-icons/fa6';
import * as LuIcons from 'react-icons/lu';
import * as LiaIcons from 'react-icons/lia';
import * as PiIcons from 'react-icons/pi';
import * as GrIcons from 'react-icons/gr';

import { useAuthenticatedUserInfo } from '../../services/Contexts/AuthenticatedUserInfoContext';
import { useMenuItems } from '../../hooks/useSidebar';
import {
	useNavVisibilityPreferences,
	useUpdateNavVisibilityPreferences,
	useResetNavVisibilityPreferences,
} from '../../hooks/useNavVisibilityPreferences';
import { hasAnyPermission } from '../../utils/permissionsUtils';
import { useCompanySettings } from '../../hooks/useCompany';
import defaultSizes from '../../utils/defaultSizes';
import { DEFAULT_WORKSPACE_SIDEBAR_BRANDING } from '../../constants/defaultWorkspaceBranding';

import LogoImage from '../../assets/logoHorizontalAligned.png';

const iconPacks = [
	GoIcons,
	FaIcons,
	MdIcons,
	AiIcons,
	IoIcons,
	Io5Icons,
	BsIcons,
	FiIcons,
	RiIcons,
	HiIcons,
	SiIcons,
	GiIcons,
	BiIcons,
	CgIcons,
	TbIcons,
	TiIcons,
	ImIcons,
	VscIcons,
	Fa6Icons,
	LuIcons,
	LiaIcons,
	PiIcons,
	GrIcons,
];

// Helper to determine if a menu item should render for the current user
function canRenderMenuItem(item, userRole, permissions) {
	const roleAllowed =
		!item.visibleToRoles ||
		item.visibleToRoles.length === 0 ||
		item.visibleToRoles.includes(userRole);
	const hasPerm =
		Array.isArray(item.permissions) && item.permissions.length > 0
			? item.permissions.some((p) =>
					hasAnyPermission(permissions, p.resource, p.actions),
				)
			: false;
	// Always allow Logout
	if (item.title === 'Logout') return true;
	return roleAllowed || hasPerm;
}

const getIcon = (iconName) => {
	if (!iconName) return null;
	for (const pack of iconPacks) {
		if (pack[iconName]) return pack[iconName];
	}
	return null;
};

const normalizeId = (id) => {
	if (!id) return null;
	if (typeof id === 'string') return id;
	if (id.$oid) return id.$oid;
	if (id.toString) return id.toString();
	return id;
};

const SideNavBar = ({
	onCollapse,
	externalCollapsed,
	isMobile,
	onMobileClose,
}) => {
	const { userRole, logout, companyId, userPermissions, isCompanyAdmin } =
		useAuthenticatedUserInfo();
	const {
		data: menuItems = [],
		isLoading: menuLoading,
		error: menuError,
	} = useMenuItems();
	const { data: companySettings } = useCompanySettings(companyId);

	// Get company branding colors with fallbacks
	const sidebarBackgroundColor =
		companySettings?.data?.settings?.branding?.sidebarBackgroundColor ||
		DEFAULT_WORKSPACE_SIDEBAR_BRANDING.sidebarBackgroundColor;
	const sidebarTextColor =
		companySettings?.data?.settings?.branding?.sidebarTextColor ||
		DEFAULT_WORKSPACE_SIDEBAR_BRANDING.sidebarTextColor;
	const sidebarHoverTextColor =
		companySettings?.data?.settings?.branding?.sidebarHoverTextColor ||
		DEFAULT_WORKSPACE_SIDEBAR_BRANDING.sidebarHoverTextColor;
	const sidebarActiveTextColor =
		companySettings?.data?.settings?.branding?.sidebarActiveTextColor ||
		DEFAULT_WORKSPACE_SIDEBAR_BRANDING.sidebarActiveTextColor;
	const [collapsed, setCollapsed] = useState(() =>
		JSON.parse(localStorage.getItem('sidebarCollapsed') || 'false'),
	);
	const [openKeys, setOpenKeys] = useState([]);
	const [isManagingVisibility, setIsManagingVisibility] = useState(false);
	const location = useLocation();

	// DB-backed nav visibility preferences
	const { data: hiddenItemIdsArr = [] } = useNavVisibilityPreferences(companyId);
	const hiddenNavItems = new Set(hiddenItemIdsArr);
	const { mutate: saveVisibility } = useUpdateNavVisibilityPreferences();
	const { mutate: resetVisibility } = useResetNavVisibilityPreferences();

	// Sync external collapsed state (driven by viewport resize in LoggedInLayout)
	useEffect(() => {
		if (externalCollapsed !== undefined) {
			setCollapsed(externalCollapsed);
		}
	}, [externalCollapsed]);

	const getItemKey = (item) =>
		item._id ? normalizeId(item._id) : String(item.order);

	const toggleItemVisibility = (key) => {
		const next = new Set(hiddenNavItems);
		if (next.has(key)) next.delete(key);
		else next.add(key);
		saveVisibility({ companyId, hiddenItemIds: [...next] });
	};

	const renderWithManageOverlay = (item, element) => {
		if (
			!isManagingVisibility ||
			item.title === 'Logout' ||
			item.title === 'Dashboard'
		)
			return element;
		const key = getItemKey(item);
		const isHidden = hiddenNavItems.has(key);
		return (
			<Box position='relative' key={item.order}>
				<Box opacity={isHidden ? 0.3 : 1} pointerEvents='none' userSelect='none'>
					{element}
				</Box>
				<Box
					position='absolute'
					top={0}
					right={0}
					bottom={0}
					display='flex'
					alignItems='center'
					pr={collapsed ? 1 : 3}
					cursor='pointer'
					onClick={() => toggleItemVisibility(key)}>
					<Tooltip
						label={isHidden ? 'Show item' : 'Hide item'}
						hasArrow
						placement='right'>
						<Box
							borderRadius='full'
							p='3px'
							bg={isHidden ? 'gray.100' : 'teal.50'}
							display='flex'
							alignItems='center'
							color={isHidden ? 'gray.400' : 'teal.500'}>
							{isHidden ? <FaEye size={11} /> : <FaEyeSlash size={11} />}
						</Box>
					</Tooltip>
				</Box>
			</Box>
		);
	};

	const toggleCollapsed = () => {
		if (isMobile && onMobileClose) {
			onMobileClose();
			return;
		}
		const next = !collapsed;
		setCollapsed(next);
		localStorage.setItem('sidebarCollapsed', JSON.stringify(next));
		onCollapse?.(next);
	};

	const handleNavClick = () => {
		if (isMobile && onMobileClose) onMobileClose();
	};

	// Helper: checks if a path is a word in the current URL
	const pathIsActive = (path, url) => {
		if (!path) return false;
		// Remove leading/trailing slashes and split both path and url into words
		const pathWord = path.replace(/^\/+|\/+$/g, '').toLowerCase();
		const urlWords = url
			.replace(/^\/+|\/+$/g, '')
			.toLowerCase()
			.split(/[\/?#&]/);
		return urlWords.some((word) => word === pathWord);
	};

	const isParentActive = (id) =>
		menuItems.some(
			(item) =>
				normalizeId(item.parent) === normalizeId(id) &&
				pathIsActive(item.path, location.pathname),
		);

	const renderChildren = (parentId) =>
		menuItems
			.filter(
				(item) =>
					normalizeId(item.parent) === normalizeId(parentId) &&
					item.render &&
					canRenderMenuItem(item, userRole, userPermissions) &&
					(isManagingVisibility || !hiddenNavItems.has(getItemKey(item))),
			)
			.map((child) => {
				const IconComp = getIcon(child.icon);
				const active = pathIsActive(child.path, location.pathname);
				return renderWithManageOverlay(
					child,
					<Box key={child.order}>
						{!collapsed && (
							<Flex
								as={Link}
								to={child.path}
								onClick={handleNavClick}
								align='center'
								pl={4}
								py={1}
								ml={8}
								mb={0}
								bg={'transparent'}
								_hover={{
									color: active
										? sidebarActiveTextColor || 'gray.900'
										: sidebarHoverTextColor || 'gray.900',
									bg: `transparent`,
								}}
								borderRadius='md'
								transition='background 0.2s'
								cursor='pointer'
								position='relative'>
								{IconComp && (
									<Box
										mr={collapsed ? 0 : 2}
										fontSize='lg'
										color={
											active
												? sidebarActiveTextColor || 'gray.900'
												: sidebarTextColor || 'gray.700'
										}>
										<IconComp
											color={
												active
													? sidebarActiveTextColor || 'gray.900'
													: sidebarTextColor || 'gray.700'
											}
										/>
									</Box>
								)}
								<Text
									m={0}
									fontWeight='medium'
									fontSize={'15px'}
									color={
										active
											? sidebarActiveTextColor || 'gray.900'
											: sidebarTextColor || 'gray.700'
									}>
									{child.title}
								</Text>
								{/* {active && (
									<Box
										position='absolute'
										left={0}
										top={0}
										bottom={0}
										width='3px'
										bg={sidebarActiveTextColor}
										borderRadius='md'
									/>
								)} */}
							</Flex>
						)}
					</Box>,
				);
			});

	const renderItems = () =>
		menuItems
			.filter(
				(item) =>
					item.render &&
					(collapsed ? !item.isParent : !item.parent) && // When collapsed, skip parents; otherwise show only top-level
					canRenderMenuItem(item, userRole, userPermissions) &&
					(item.title === 'Logout' ||
						isManagingVisibility ||
						!hiddenNavItems.has(getItemKey(item))),
			)
			.map((item) => {
				const IconComp = getIcon(item.icon);
				const activeMain =
					pathIsActive(item.path, location.pathname) || isParentActive(item._id);

				let content;

				// This is menu items with child 'Parents'
				if (item.isParent) {
					content = (
						<Flex
							role='group'
							align='center'
							py={collapsed ? 1 : 0}
							px={collapsed ? 2 : 4}
							mb={collapsed ? 1 : 2}
							borderTopLeftRadius={'0px'}
							borderTopRightRadius={'0px'}
							borderBottomLeftRadius={'0px'}
							borderBottomRightRadius={'0px'}
							overflow={'hidden'}
							transition='background 0.2s'
							cursor='pointer'
							position='relative'
							boxShadow={activeMain ? 'none' : undefined}
							_hover={{
								color: activeMain
									? sidebarActiveTextColor || 'gray.900'
									: sidebarHoverTextColor || 'gray.900',
							}}
							onClick={() => {
								setOpenKeys((curr) => (curr.includes(item.order) ? [] : [item.order]));
							}}>
							{IconComp && (
								<Box
									mr={collapsed ? 0 : 3}
									color={
										activeMain
											? sidebarActiveTextColor || 'gray.900'
											: sidebarTextColor || 'gray.700'
									}
									_groupHover={{ color: sidebarHoverTextColor || 'gray.900' }}>
									<IconComp fontSize={collapsed ? '26px' : '25px'} />
								</Box>
							)}
							{!collapsed && (
								<Text
									flex='1'
									m={0}
									fontSize={'15px'}
									fontWeight={activeMain ? 'medium' : 'medium'}
									color={
										activeMain
											? sidebarActiveTextColor || 'gray.900'
											: sidebarTextColor || 'gray.700'
									}
									_groupHover={{ color: sidebarHoverTextColor || 'gray.900' }}>
									{item.title}
								</Text>
							)}
							{/* This is the arrow right is parent and not collapsed */}
							{item.isParent && !collapsed && (
								<IconButton
									size='xs'
									aria-label='toggle'
									icon={
										openKeys.includes(item.order) ? <FaChevronLeft /> : <FaChevronRight />
									}
									variant='ghost'
									color={
										activeMain
											? sidebarActiveTextColor || 'gray.900'
											: sidebarTextColor || 'gray.700'
									}
									_hover={{
										color: activeMain
											? sidebarActiveTextColor || 'gray.900'
											: sidebarHoverTextColor || 'gray.900',
									}}
									ml={2}
									// Prevents arrow click from triggering navigation
									onClick={(e) => {
										e.stopPropagation();
										setOpenKeys((curr) =>
											curr.includes(item.order) ? [] : [item.order],
										);
									}}
								/>
							)}
							{/* {activeMain && (
								<Box
									position='absolute'
									left={0}
									top={0}
									bottom={0}
									width='4px'
									bg={sidebarActiveTextColor}
									borderRadius='md'
								/>
							)} */}
							{item.badge && !collapsed && (
								<Badge
									ml={2}
									bg='gray.200'
									color={sidebarTextColor || 'gray.900'}>
									{item.badge}
								</Badge>
							)}
						</Flex>
					);
				}

				// This is menu items without child 'Not Parents'
				if (!item.isParent) {
					content = (
						<Flex
							role='group'
							align='center'
							py={collapsed ? 1 : 0}
							px={collapsed ? 2 : 4}
							mb={collapsed ? 1 : 2}
							bg={activeMain ? 'transparent' : 'transparent'}
							_hover={{
								color: activeMain
									? sidebarActiveTextColor
									: sidebarHoverTextColor || 'gray.900',
								...(activeMain ? {} : { bg: `transparent` }),
							}}
							borderTopLeftRadius={'0px'}
							borderTopRightRadius={'0px'}
							borderBottomLeftRadius={'0px'}
							borderBottomRightRadius={'0px'}
							overflow={'hidden'}
							transition='background 0.2s'
							cursor='pointer'
							position='relative'
							boxShadow={'none'}>
							{IconComp && (
								<Box
									mr={collapsed ? 0 : 3}
									color={
										activeMain
											? sidebarActiveTextColor || 'gray.900'
											: sidebarTextColor || 'gray.700'
									}
									_groupHover={{ color: sidebarHoverTextColor || 'gray.900' }}>
									<IconComp fontSize={collapsed ? '26px' : '25px'} />
								</Box>
							)}
							{!collapsed && (
								<Text
									flex='1'
									m={0}
									fontWeight={activeMain ? 'medium' : 'medium'}
									color={
										activeMain
											? sidebarActiveTextColor || 'gray.900'
											: sidebarTextColor || 'gray.700'
									}
									_groupHover={{ color: sidebarHoverTextColor || 'gray.900' }}>
									{item.title}
								</Text>
							)}
							{/* {activeMain && !collapsed && (
								<Box
									position='absolute'
									left={'0px'}
									top={'25%'}
									bottom={0}
									width='3px'
									// height={'20px'}
									bg={sidebarActiveTextColor}
									borderRadius='md'
								/>
							)} */}
						</Flex>
					);
				}

				if (item.isParent) {
					return renderWithManageOverlay(
						item,
						<Box key={item.order} bg={'transparent'}>
							{content}
							<Collapse in={openKeys.includes(item.order)} animateOpacity>
								<VStack align='stretch' spacing={1} pl={collapsed ? 2 : 2} mt={1}>
									{renderChildren(item._id)}
								</VStack>
							</Collapse>
						</Box>,
					);
				}

				if (item.title === 'Logout') {
					return (
						<Box
							mt={collapsed ? 0 : 2}
							key={item.order}
							onClick={() => {
								handleNavClick();
								logout();
							}}>
							{content}
						</Box>
					);
				}

				const targetLink = item.title === 'Dashboard' ? '/dashboard' : item.path;
				return renderWithManageOverlay(
					item,
					<Box key={item.order} bg={'transparent'}>
						<ChakraLink
							as={Link}
							to={targetLink}
							onClick={handleNavClick}
							style={{ textDecoration: 'none' }}>
							{content}
						</ChakraLink>
					</Box>,
				);
			});

	if (menuLoading)
		return (
			<Flex height='100%' align='center' justify='center'>
				<Spinner size='lg' />
			</Flex>
		);
	if (menuError)
		return (
			<Alert status='error' variant='subtle'>
				{menuError.message}
			</Alert>
		);

	return (
		<Flex
			direction='column'
			w={
				collapsed ? defaultSizes.sidebarCollapsedWidth : defaultSizes.sidebarWidth
			}
			minW={
				collapsed ? defaultSizes.sidebarCollapsedWidth : defaultSizes.sidebarWidth
			}
			maxW={
				collapsed ? defaultSizes.sidebarCollapsedWidth : defaultSizes.sidebarWidth
			}
			h='100vh'
			bg={sidebarBackgroundColor}
			color={sidebarTextColor || 'gray.900'}
			transition='width .25s cubic-bezier(.4,0,.2,1)'>
			{/* Logo Section */}
			<Flex
				align='center'
				justify={collapsed ? 'start' : 'start'}
				// justifyContent={'space-between'}
				h={defaultSizes.headerHeight}
				pt={'2px'}
				pl={collapsed ? '5px' : '32px'}
				pos={'relative'}
				bg={sidebarBackgroundColor}>
				<Image
					src={companySettings?.data?.settings?.branding?.logo || LogoImage}
					alt='Company Logo'
					h='auto'
					w={collapsed ? '100%' : 'auto'}
					maxW={collapsed ? '100%' : '220px'}
					maxH={collapsed ? '50px' : '60px'}
					objectFit='contain'
				/>

				{/* Collapse Controls */}
				<Flex
					position={'absolute'}
					right={isMobile ? '12px' : '-10px'}
					top={0}
					direction='column'
					align='center'
					justify='center'
					py={4}>
					<Tooltip
						label={
							isMobile
								? 'Close Menu'
								: collapsed
									? 'Expand Sidebar'
									: 'Collapse Sidebar'
						}
						hasArrow>
						<IconButton
							aria-label={isMobile ? 'Close Menu' : 'Toggle Sidebar'}
							icon={
								isMobile ? (
									<FaTimes />
								) : collapsed ? (
									<FaChevronRight />
								) : (
									<FaChevronLeft />
								)
							}
							onClick={toggleCollapsed}
							mt={0}
							size='sm'
							colorScheme='gray'
							variant='solid'
							borderRadius='full'
							transition='all 0.2s'
						/>
					</Tooltip>
				</Flex>
			</Flex>

			{/* Menu Items */}
			<Box flex='1' overflowY='auto' py={4} px={collapsed ? 2 : 4}>
				{isManagingVisibility && !collapsed && (
					<Box
						mb={3}
						px={2}
						py={2}
						bg='teal.50'
						borderRadius='md'
						borderLeft='3px solid'
						borderLeftColor='teal.400'>
						<Text fontSize='xs' color='teal.700' fontWeight='600'>
							Click the eye icon to hide / show nav items
						</Text>
						{hiddenNavItems.size > 0 && (
							<Text
								fontSize='xs'
								color='teal.500'
								cursor='pointer'
								mt={1}
								_hover={{ textDecoration: 'underline' }}
								onClick={() => resetVisibility(companyId)}>
								Reset all ({hiddenNavItems.size} hidden)
							</Text>
						)}
					</Box>
				)}
				<VStack spacing={1} align={collapsed ? 'center' : 'stretch'} fontSize={'16px'} fontWeight={'800'}>
					{renderItems()}
				</VStack>
			</Box>

			{/* Customize Nav Button */}
			{isCompanyAdmin && (
				<>
					<Box px={collapsed ? 2 : 4} pb={2}>
						<Tooltip
							label={isManagingVisibility ? 'Done editing' : 'Customize navigation'}
							hasArrow
							placement='top'>
							<Flex
								align='center'
								py={2}
								px={2}
								cursor='pointer'
								borderRadius='md'
								bg={isManagingVisibility ? 'teal.50' : 'transparent'}
								border='1px solid'
								borderColor={isManagingVisibility ? 'teal.200' : 'transparent'}
								_hover={{
									bg: isManagingVisibility ? 'teal.100' : 'gray.50',
								}}
								onClick={() => setIsManagingVisibility((p) => !p)}
								transition='all 0.2s'>
								<Box
									mr={collapsed ? 0 : 2}
									color={isManagingVisibility ? 'teal.500' : 'gray.400'}
									display='flex'
									alignItems='center'>
									{isManagingVisibility ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
								</Box>
								{!collapsed && (
									<Text
										fontSize='xs'
										color={isManagingVisibility ? 'teal.600' : 'gray.400'}
										fontWeight='medium'
										m={0}>
										{isManagingVisibility ? 'Save' : 'Customize Nav'}
									</Text>
								)}
							</Flex>
						</Tooltip>
					</Box>

					<Divider borderColor={sidebarHoverTextColor} opacity={0.3} />
				</>
			)}
		</Flex>
	);
};

export default SideNavBar;
